<template>
  <div class="btn-blocked-on select-item-btn btn-group" v-if="lockedOn">
    <fa v-if="lockedCheck" icon="check" tabindex="0" :aria-label="`${label} ${$t('alwaysOn')}`"></fa>
    <div v-else class="bbo-text" tabindex="0" :aria-label="`${label} ${$t('alwaysOn')}`">{{ labelOn }}</div>
  </div>
  <div v-else>
    <span>{{ currentValueLabel }}</span>
    <div
      class="select-item-btn btn-group btn-toggle pcm-switch"
      @click="toggleValue(null, $event)"
      @keydown.space.prevent="toggleValue(null, $event)"
      @keydown.enter.prevent="toggleValue(null, $event)"
      :class="{
        'default-neutral': value === null,
        'btn-toggle-on': value === true,
        'switch-standard': !reversePosition,
      }"
      role="switch"
      :aria-label="switchAriaLabel"
      :aria-checked="switchAriaChecked"
      tabindex="0"
    >
      <button
        class="btn btn-xs btn-yes"
        :class="{ 'btn-primary': value === true, 'btn-default': !value }"
        tabindex="-1"
        aria-hidden="true"
        @click="toggleValue(true, $event)"
      >
        <div class="switch-content"></div>
      </button>
      <span></span>
      <button
        class="btn btn-xs btn-no btn-switch-no"
        :class="{ 'btn-primary': value === false, 'btn-default': value }"
        tabindex="-1"
        aria-hidden="true"
        @click="toggleValue(false, $event)"
      >
        <div class="switch-content btn-switch-no"></div>
      </button>
    </div>
  </div>
</template>
<script>
import { i18nMixin } from 'shared/i18n/i18n-mixin';
export default {
  mixins: [i18nMixin],
  props: {
    value: {
      type: Boolean,
    },
    labelOn: {
      type: String,
      default: () => 'On',
    },
    labelOff: {
      type: String,
      default: () => 'Off',
    },
    labelMixed: {
      type: String,
      default: () => 'Mixed',
    },
    label: {
      type: String,
      default: () => '',
    },
    lockedOn: {
      type: Boolean,
      default: () => false,
    },
    lockedCheck: {
      type: Boolean,
      default: () => false,
    },
    reversePosition: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      lastEvent: null,
    };
  },
  computed: {
    switchAriaLabel() {
      if (this.value === false) {
        return this.$t('switchAria', { nextValue: this.$t('turnOnLabel'), category: this.label });
      } else {
        return this.$t('switchAria', { nextValue: this.$t('turnOffLabel'), category: this.label });
      }
    },
    switchAriaChecked() {
      if (this.value === true) {
        return 'true';
      }
      if (this.value === false) {
        return 'false';
      }
      return 'mixed';
    },
    currentValueLabel() {
      if (this.value === true) {
        return this.labelOn;
      }
      if (this.value === false) {
        return this.labelOff;
      }
      return this.labelMixed;
    },
  },
  methods: {
    toggleValue(choice, event) {
      if (event === this.lastEvent) {
        //Can't handle this with preventPropagation as custom JS may need to get
        // those events
        return;
      }
      this.lastEvent = event;

      if (this.value == null) {
        if (choice != null) {
          this.$emit('input', choice);
        } else {
          this.$emit('input', false);
        }
      } else {
        this.$emit('input', !this.value);
      }
    },
  },
};
</script>
<style lang="scss">
$color-off: #aeb5bb;

.select-item-btn {
  cursor: pointer;
  margin-left: 1rem;
}

.select-item-btn.btn-group > .btn:first-child:not(:last-child):not(.dropdown-toggle) {
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
}

.select-item-btn.btn-group > .btn:last-child:not(:first-child),
.btn-toggle:not(.default-neutral) .btn-xs {
  border-top-left-radius: 30px;
  border-bottom-left-radius: 30px;
}
.btn-group > .btn:last-child:not(:first-child),
.btn-toggle:not(.default-neutral) .btn-xs {
  border-radius: 30px;
}
.btn-group:not(.default-neutral) > .btn.btn-primary:first-child:not(:last-child):not(.dropdown-toggle) {
  margin-right: -22px;
  padding-right: 14px;
  padding-left: 14px;
}

.btn-toggle > span {
  visibility: hidden;
  display: inline-block;
  position: relative;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50px;
  margin: 2px 0px;
  border: 1px solid #b4b4b4;
}

.btn-toggle {
  border-radius: 50px;
  background-color: $color-off;
}

.btn-default.btn-no {
  background-color: #ccc;
}

.btn-group:not(.default-neutral) > .btn.btn-primary:last-child:not(:first-child) {
  margin-left: -22px;
  padding-right: 14px;
  padding-left: 14px;
}

.select-item-btn .btn-default.btn-no,
.select-item-btn .btn-default.btn-yes,
.select-item-btn .btn-toggle:not(.default-neutral) {
  color: transparent;
}

.btn-toggle:not(.default-neutral) .btn-primary.btn-xs > span {
  color: black;
}

.btn-toggle.default-neutral .btn-xs {
  border: 2px solid transparent;
}

.btn-toggle:not(.default-neutral) .btn-xs {
  border: 2px solid $color-off;
  border-radius: 30px;
}

.btn-toggle > .btn-primary > .text {
  color: #fff;
}

.btn-toggle.default-neutral > .btn-primary {
  background-color: $color-off;
}

.btn-toggle.default-neutral > .btn-primary > span {
  color: black;
}

.btn-toggle.default-neutral > span {
  visibility: visible;
}

.btn-toggle:not(.default-neutral) {
  background-color: $color-off;
}

.btn-toggle.default-neutral > span {
  visibility: visible;
}

.btn-toggle.default-neutral .btn-xs {
  background-color: transparent;
  border-color: transparent;
}

.btn-toggle.default-neutral .btn-xs > span {
  color: black;
}

.btn.btn-yes.btn-default {
  background-color: $color-off;
}

.btn-blocked-on {
  cursor: default;
  border-radius: 30px;
  height: 25px;
  text-align: center;
  padding-top: 4px;
  min-width: 25px;
  padding-left: 4px;
  padding-right: 4px;
}

.btn-blocked-on.bbo-text {
  width: 35px;
  font-size: 11px;
  padding-left: 4px;
  padding-right: 4px;
}
.switch-content {
  height: 1.8rem;
  width: 0.6rem;
}

.default-neutral {
  .switch-content {
    width: 0.4rem;
  }
}

.switch-standard {
  transform: scaleX(-1);
}

.pcm-switch.select-item-btn {
  .btn-xs:not(.btn-primary) {
    border-radius: 50px;
    padding: 1px 5px;
  }
}
</style>
